import debounce from '../../shared/helpers/debounce';
import validateEmail from '../../shared/helpers/validateEmail';
import actionsResponse from '../../shared/helpers/actionsResponse';
import createElement from '../../shared/helpers/createElement';
import { FormResponseType } from '../../Services/Connection/types/FormResponseType';
import FormBuilder from '../FormBuilder/FormBuilder';
import ConnectionService from '../../Services/Connection/Connection.service';
import cloneTemplate from '../../shared/helpers/cloneTemplate';
import { importPasswordResetChunkOnClick } from '../PasswordReset/Chunk';

export default class Connection {
  private static modalConnectionService = new ConnectionService();

  private static formConnectionSelector = '#connection-form';

  private static formConnectionInputsWrapperSelector =
    '#connection-form-inputs-wrapper';

  private static formConnectionRegisterButtonSelector = '#register-button';

  private formConnectionSelector = '.js-connection';

  constructor() {
    document
      .querySelectorAll<HTMLElement>(this.formConnectionSelector)
      .forEach((formConnection) =>
        Connection.handleEmail(formConnection, true)
      );
  }

  static handleEmail = (
    parentElement: HTMLElement,
    shouldRegisterInModal = false
  ) => {
    const formConnection = shouldRegisterInModal
      ? (parentElement as HTMLFormElement)
      : parentElement.querySelector<HTMLFormElement>(
          Connection.formConnectionSelector
        );
    const formInputsElement: HTMLFormElement = parentElement.querySelector(
      Connection.formConnectionInputsWrapperSelector
    );
    if (!formConnection || !formInputsElement) return parentElement;

    const emailInput: HTMLInputElement = formConnection.querySelector(
      'input[name="email"]'
    );
    if (!emailInput) return parentElement;

    const registerButton = cloneTemplate(
      this.formConnectionRegisterButtonSelector
    );

    let previousEmail = '';
    emailInput.addEventListener(
      'input',
      debounce(async () => {
        const email = emailInput.value;
        if (email === previousEmail) return;

        previousEmail = email;
        const inputGroup = emailInput.parentElement;

        const toggleLoader = (show: boolean) => {
          const isLoader =
            inputGroup.nextElementSibling?.classList.contains(
              'c-modal__loader'
            );

          if (show && !isLoader) {
            inputGroup.after(Connection.getLoader());
          }

          if (!show && isLoader) {
            inputGroup.nextElementSibling?.remove();
          }
        };

        formInputsElement.innerHTML = '';
        formConnection.removeAttribute('action');
        formConnection.removeAttribute('method');
        formConnection.classList.remove('c-connectionForm--contact-register');

        const isEmailValid = validateEmail(email);
        if (isEmailValid !== true) return;
        toggleLoader(true);
        const checkEmailExist =
          await this.modalConnectionService.checkEmailExist(email);
        const isEmailExists = checkEmailExist?.exists;
        const accountPage = document.querySelector(
          '.c-blocLoyaltyLeft__container'
        );

        if (shouldRegisterInModal) {
          if (isEmailExists) {
            const formResponse =
              await this.modalConnectionService.getFormConnection();
            toggleLoader(false);
            if (!(isEmailExists && accountPage)) {
              await this.handleCreateForm(parentElement, formResponse);
            }
          } else {
            formConnection.classList.add('c-connectionForm--contact-register');
            formInputsElement.append(registerButton);
            registerButton.addEventListener(
              'modal:connection:open',
              ({ detail }: CustomEvent) => {
                const modal = detail.modalElement as HTMLElement;
                if (!modal) return;

                const modalEmail = modal.querySelector<HTMLInputElement>(
                  'input[name="email"]'
                );
                if (!modalEmail) return;

                modalEmail.value = email;
                modalEmail.focus();
                modalEmail.setAttribute('readonly', '');
                modalEmail.setAttribute('tabindex', '-1');
                modalEmail.dispatchEvent(new Event('input', { bubbles: true }));
              }
            );
            toggleLoader(false);
          }
        } else {
          const formResponse = await (isEmailExists
            ? this.modalConnectionService.getFormConnection()
            : this.modalConnectionService.getFormRegistration(email));
          toggleLoader(false);
          await this.handleCreateForm(parentElement, formResponse);
        }
        if (isEmailExists) importPasswordResetChunkOnClick();
      }, 300)
    );

    if (emailInput.value) {
      emailInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const messagesElement = createElement('div', {});
    formConnection.addEventListener('submit', async (e) => {
      e.preventDefault();
      messagesElement.innerHTML = '';
      const formData = new FormData(formConnection);
      let formConnectionAction: string;
      const submitButton = e.submitter;

      if (submitButton && submitButton.classList.contains('js-submitGuest')) {
        // Auth Guest
        formConnectionAction = submitButton.dataset.action;
      } else {
        const realEmailInput = formConnection.querySelector<HTMLInputElement>(
          'input[type="hidden"][name*="email"]'
        );
        // On récupère la valeur du champ "email" qui a été saisie par l'utilisateur, et on l'attribut le FormData correspondant au champ caché
        // dont le name dépend du formulaire (checkout_login / checkout_register / ...)
        formData.set(realEmailInput.name, formData.get('email'));
        formData.delete('email');
        formConnectionAction = formConnection.action;
      }

      const { actions } = await this.modalConnectionService.sendForm(
        formData,
        formConnectionAction
      );

      const messages = actionsResponse(actions);
      if (messages?.length) {
        messagesElement.innerHTML = messages;
        const removeMessages = () => {
          messagesElement.remove();
          formInputsElement.removeEventListener('submit', removeMessages);
        };
        formInputsElement.addEventListener('input', removeMessages);
        formConnection.prepend(messagesElement);
      }
    });

    return parentElement;
  };

  static getLoader = () => {
    const loader = createElement('div', { class: 'c-modal__loader' });
    loader.innerHTML = `
      <span class='dot-flashing'>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </span>
    `;
    return loader;
  };

  private static handleCreateForm = async (
    parentElement: HTMLElement,
    formResponse: FormResponseType
  ) => {
    const formElement =
      `#${parentElement.id}` === Connection.formConnectionSelector
        ? (parentElement as HTMLFormElement)
        : parentElement.querySelector<HTMLFormElement>(
            Connection.formConnectionSelector
          );
    const formInputsElement: HTMLElement = formElement.querySelector(
      this.formConnectionInputsWrapperSelector
    );
    if (!formElement || !formInputsElement) return;

    const formBuilder = new FormBuilder(formResponse);
    await formBuilder.init(formElement);

    formInputsElement.append(
      ...formBuilder.formExtra.top,
      ...formBuilder.formInputs,
      ...formBuilder.formExtra.bottom
    );

    const submitButton = formInputsElement.querySelector(
      'button[type="submit"]'
    ) as HTMLButtonElement;
    if (!submitButton) return;

    submitButton.classList.add('c-connectionForm__button');
    submitButton.setAttribute('disabled', '');

    formInputsElement.append(submitButton);

    formInputsElement.addEventListener('input', () => {
      if (formElement.checkValidity()) {
        submitButton.removeAttribute('disabled');
      } else {
        submitButton.setAttribute('disabled', '');
      }
    });
  };
}
